// AB#2449 — per-country address metadata, libaddressinput-style: a pure DATA module driving the
// address composite's member order, labels, required flags and postal-code shape. The value model
// is the international superset { addressLines: string[], dependentLocality, locality,
// administrativeArea, postalCode, sortingCode, countryCode } — country metadata only changes how
// it RENDERS, never what is stored. GB is fully specified this sprint; 'ZZ' is the generic
// fallback every other country gets until its row is added (an open data problem, not a service).

export interface CountryAddressMeta {
    /** Rows of member names, in render order; members in one row lay out side by side. */
    order: string[][]
    labels: Record<string, string>
    required: string[]
    /** Anchored regex the postal code must match (validated on edit; never blocks render). */
    postalCodePattern?: string
}

export const ADDRESS_METADATA: Record<string, CountryAddressMeta> = {
    GB: {
        order: [['addressLines'], ['locality'], ['administrativeArea'], ['postalCode']],
        labels: {
            addressLines: 'Address',
            locality: 'Town / City',
            administrativeArea: 'County',
            postalCode: 'Postcode',
        },
        required: ['addressLines', 'locality', 'postalCode'],
        // The standard tolerant GB shape (outward + inward), case-insensitive, optional space.
        postalCodePattern: '^[A-Za-z]{1,2}\\d[A-Za-z\\d]?\\s?\\d[A-Za-z]{2}$',
    },
    // The generic fallback: every member offered, nothing required, no postcode shape enforced.
    ZZ: {
        order: [['addressLines'], ['dependentLocality'], ['locality'], ['administrativeArea'], ['postalCode', 'sortingCode']],
        labels: {
            addressLines: 'Address',
            dependentLocality: 'District',
            locality: 'City',
            administrativeArea: 'Region',
            postalCode: 'Postal code',
            sortingCode: 'Sorting code',
        },
        required: [],
    },
}

export function countryAddressMeta(countryCode: string | undefined): CountryAddressMeta {
    return (countryCode && ADDRESS_METADATA[countryCode]) || ADDRESS_METADATA['ZZ']!
}

/** The composite value shape (all members optional; unknown keys tolerated). */
export type AddressValue = {
    addressLines?: unknown
    dependentLocality?: unknown
    locality?: unknown
    administrativeArea?: unknown
    postalCode?: unknown
    sortingCode?: unknown
    countryCode?: unknown
} & Record<string, unknown>

/**
 * The display lines for a stored address value, in the country's declared order (address lines
 * first, then the ordered scalar members). Empty members are omitted; the country itself is the
 * CALLER's concern (it may want the resolved country name, not the code).
 */
export function formatAddressLines(value: AddressValue): string[] {
    const meta = countryAddressMeta(typeof value.countryCode === 'string' ? value.countryCode : undefined)
    const lines: string[] = []
    for (const row of meta.order) {
        const parts: string[] = []
        for (const member of row) {
            if (member === 'addressLines') {
                const raw = value.addressLines
                if (Array.isArray(raw)) {
                    for (const l of raw) { if (typeof l === 'string' && l.trim()) { lines.push(l.trim()) } }
                }
                continue
            }
            const v = value[member]
            if (typeof v === 'string' && v.trim()) { parts.push(v.trim()) }
        }
        if (parts.length > 0) { lines.push(parts.join(' ')) }
    }
    return lines
}
